import { Component, inject, input, output, signal, OnInit } from "@angular/core";
import { RouterModule } from "@angular/router";
import { AudioStreamComponent } from "@components/audio-stream/audio-stream";
import { Container } from "@components/container/container";
import { Header } from "@components/header/header";
import { SearchInput } from "@components/search-input/search-input";
import { EducationLevelGroup } from "@domain/EducationLevel";
import { HomophoneGroup } from "@domain/HomophoneGroup";
import { AnalyticsService } from "@services/analytics.service";
import { HomophoneGroupService } from "@services/homophone-group.service";

@Component({
  selector: "app-homophone-groups-selection",
  imports: [Header, RouterModule, Container, SearchInput, AudioStreamComponent],
  template: `
    @let isLoading = homophoneGroupService.isLoading();
    @let minimumNumberOfHomophoneGroupsToSelect = selectedEducationLevelGroup().numberOfHomophoneGroups.min;
    @let maximumNumberOfHomophoneGroupsToSelect = selectedEducationLevelGroup().numberOfHomophoneGroups.max;
    @let listedhomophoneGroups = homophoneGroups();
    <app-container>
      <app-header (quitExerciser)="quitExerciser.emit()">
        <a class="header__logo-link" [routerLink]="['/']">
          <img class="header__logo-img" src="assets/logo/alloprof.svg" alt="logo" aria-hidden="true" />
        </a>
      </app-header>
      <div class="homophone-groups-selection card card-large loading-container">
        <h2 class="homophone-groups-selection__title title-h4" data-testid="homophone-groups-selection-title">
          @if (minimumNumberOfHomophoneGroupsToSelect === maximumNumberOfHomophoneGroupsToSelect) {
            Sélectionne {{ minimumNumberOfHomophoneGroupsToSelect }} groupes d’homophones.
          } @else {
            Sélectionne jusqu’à {{ maximumNumberOfHomophoneGroupsToSelect }} groupes d’homophones.
          }
        </h2>

        @if (isLoading) {
          <div class="loading-mask">
            <p class="subtitle-3">Chargement...</p>
          </div>
        }

        <div class="homophone-groups-selection__search-input">
          <app-search-input [searchTerm]="searchTerm()" [isDisabled]="isLoading" (changeSearchTerm)="handleSearchTermChange($event)" />
        </div>

        @if (listedhomophoneGroups && listedhomophoneGroups!.length > 0) {
          <div class="homophone-groups-selection__scroll-container">
            <ul class="homophone-groups-selection__homophone-group-list">
              @for (homophoneGroup of listedhomophoneGroups; track homophoneGroup) {
                @let isHomophoneGroupSelected = selectedHomophoneGroups().includes(homophoneGroup);

                <li class="homophone-groups-selection__homophone-group">
                  <input
                    type="checkbox"
                    name="homophone-group"
                    [id]="'school-homophone-groups-selection-' + homophoneGroup.id"
                    class="homophone-groups-selection__checkbox"
                    [disabled]="hasReachMaximumOfSelectedHomophoneGroups() && !isHomophoneGroupSelected"
                    [checked]="isHomophoneGroupSelected"
                    (change)="selectHomophoneGroup(homophoneGroup)"
                  />
                  <div class="homophone-groups-selection__wrapper btn btn-secondary">
                    <app-audio-stream
                      class="homophone-groups-selection__audio"
                      [audioFileName]="homophoneGroup.audioFileName"
                      (playAudio)="handlePlayHomophoneGroupAudio(homophoneGroup)"
                    />
                    <label [htmlFor]="'school-homophone-groups-selection-' + homophoneGroup.id" class="homophone-groups-selection__selector">
                      <span class="homophone-groups-selection__name">Mots qui sonnent comme « {{ homophoneGroup.pronunciation }} »</span>
                      <span class="homophone-groups-selection__phonetics">{{ homophoneGroup.phonetics }}</span>
                    </label>
                  </div>
                </li>
              }
            </ul>
          </div>
        } @else {
          <div class="homophone-groups-selection__empty">
            <p class="body-3">Il n’y a aucun homophone qui correspond à votre recherche.</p>
          </div>
        }
      </div>
      <button
        type="button"
        [disabled]="!hasReachMinimumOfSelectedHomophoneGroups()"
        class="btn btn-primary homophone-groups-selection__confirmation"
        (click)="handleSelectedHomophoneGroupsConfirmation()"
      >
        Commencer
      </button>
    </app-container>
  `,
  styles: `
    @use "responsive" as *;
    @use "visuallyhidden" as *;

    .header__logo-link {
      margin: 0 auto;
    }

    .header__logo-img {
      display: block;
      width: 110px;
      height: 24px;
    }

    .homophone-groups-selection__scroll-container {
      overflow-y: auto;
      flex: 1;
      min-height: 0;
      padding-bottom: 48px;
      padding-right: 8px;
    }

    /* WebKit-based browsers (Chrome, Edge, Safari, etc.) */
    .homophone-groups-selection__scroll-container::-webkit-scrollbar {
      width: 8px;
    }

    .homophone-groups-selection__scroll-container::-webkit-scrollbar-track {
      background: transparent;
    }

    .homophone-groups-selection__scroll-container::-webkit-scrollbar-thumb {
      background: #f5f3ef;
      border-radius: 100px;
    }

    .homophone-groups-selection__scroll-container::-webkit-scrollbar-thumb:hover {
      background: #e4e0d8;
    }

    /* Firefox */
    .homophone-groups-selection__scroll-container {
      scrollbar-width: 9px;
      scrollbar-color: #f5f3ef transparent;
    }

    .homophone-groups-selection {
      margin: 0 auto 52px auto;
      padding: 24px 16px 0px 16px;
      height: 532px;
      display: flex;
      flex-direction: column;

      @include screenWidth("from-tablet") {
        padding: 48px 80px 0px 80px;
      }
    }

    .homophone-groups-selection__empty {
      margin-top: 38px;
      width: 100%;

      @include screenWidth("from-tablet") {
        margin-top: 48px;
      }

      > p {
        text-align: center;
      }
    }

    .homophone-groups-selection__title {
      margin-bottom: 32px;
      text-align: center;
    }

    .homophone-groups-selection__search-input {
      margin-bottom: 24px;
    }

    .homophone-groups-selection__homophone-group-list {
      display: flex;
      flex-direction: column;
      flex-wrap: nowrap;
      gap: 16px;
      list-style-type: none;
      padding: 0;
      margin: 0;
      flex: 1;
      min-height: 0;

      @include screenWidth("from-desktop") {
        flex-direction: row;
        flex-wrap: wrap;
        align-items: stretch;
      }
    }

    .homophone-groups-selection__homophone-group {
      margin-bottom: 2px;
      display: flex;
      flex: 1;

      @include screenWidth("from-desktop") {
        width: calc(50% - 8px);
        flex: unset;
      }
    }

    .homophone-groups-selection__checkbox {
      @include visuallyhidden();
      position: relative;
    }

    .homophone-groups-selection__wrapper {
      position: relative;
      width: 100%;
      text-align: left;
      border-radius: 20px;
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 12px;
      transition:
        background-color 1s,
        border-color 1s,
        box-shadow 1s;

      @include screenWidth("to-tablet") {
        padding: 12px;
      }
    }

    .homophone-groups-selection__homophone-group {
      &:has(input[type="checkbox"]:checked) .homophone-groups-selection__wrapper {
        color: #152942;
        background-color: #ff873d;
        border-color: #d65100;
        box-shadow: 0 4px 0 #d65100;
      }

      &:has(input[type="checkbox"]:disabled) .homophone-groups-selection__wrapper {
        color: #15294245;
        background-color: #d1dde0;
        border-color: #b3c7cb;
        box-shadow: 0 4px 0 #b3c7cb;
      }
    }

    .homophone-groups-selection__audio {
      position: relative;
      z-index: 2;
    }

    .homophone-groups-selection__selector {
      flex: 1;
      background: transparent;
      border: none;
      padding: 0;
      display: flex;
      flex-direction: column;
      gap: 4px;
      cursor: pointer;
      position: static;

      &::after {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 1;
        border-radius: 20px;
      }
    }

    .homophone-groups-selection__phonetics {
      color: #152942;
      opacity: 65%;
    }

    .homophone-groups-selection__confirmation {
      display: block;
      margin: 0 auto;
    }
  `,
})
export class HomophoneGroupSelection implements OnInit {
  homophoneGroupService = inject(HomophoneGroupService);
  private analyticsService = inject(AnalyticsService);

  selectedEducationLevelGroup = input.required<EducationLevelGroup>();

  quitExerciser = output<void>();
  confirmHomephoneGroupsSelection = output<HomophoneGroup[]>();

  homophoneGroups = signal<HomophoneGroup[] | undefined>(undefined);
  searchTerm = signal<string>("");
  selectedHomophoneGroups = signal<HomophoneGroup[]>([]);

  ngOnInit() {
    this.homophoneGroups.set(this.homophoneGroupService.searchHomophoneGroup(this.searchTerm()));
  }

  handleSearchTermChange(newSearchTerm: string) {
    this.searchTerm.set(newSearchTerm);
    this.homophoneGroups.set(this.homophoneGroupService.searchHomophoneGroup(newSearchTerm));
  }

  handleSelectedHomophoneGroupsConfirmation() {
    this.confirmHomephoneGroupsSelection.emit(this.selectedHomophoneGroups());
  }

  selectHomophoneGroup(newSelectedHomophoneGroup: HomophoneGroup) {
    this.selectedHomophoneGroups.update(currentValue => {
      if (currentValue.includes(newSelectedHomophoneGroup)) {
        return currentValue.filter(homophoneGroup => homophoneGroup.id !== newSelectedHomophoneGroup.id);
      }

      return [...currentValue, newSelectedHomophoneGroup];
    });
  }

  protected handlePlayHomophoneGroupAudio(homophoneGroup: HomophoneGroup) {
    this.analyticsService.trackHearWord(homophoneGroup.pronunciation, [homophoneGroup], "selection_groupe_homophone");
  }

  protected hasReachMinimumOfSelectedHomophoneGroups(): boolean {
    const minimumNumberOfSelectedHomophoneGroups = this.selectedEducationLevelGroup().numberOfHomophoneGroups.min;
    const numberOfSelectedHomophoneGroups = this.selectedHomophoneGroups().length;

    return numberOfSelectedHomophoneGroups >= minimumNumberOfSelectedHomophoneGroups;
  }

  protected hasReachMaximumOfSelectedHomophoneGroups(): boolean {
    const maximumNumberOfSelectedHomophoneGroups = this.selectedEducationLevelGroup().numberOfHomophoneGroups.max;
    const numberOfSelectedHomophoneGroups = this.selectedHomophoneGroups().length;

    return numberOfSelectedHomophoneGroups >= maximumNumberOfSelectedHomophoneGroups;
  }
}
